/*
 * Outlet product notice
 * ----------------------
 * On a regular product page, looks up whether an OUTLET-/DEMO-/RETUR- variant
 * of the same SKU exists and, if so, renders a small box linking to it.
 *
 * Lookup strategy (storefront only, no app/metafields needed):
 *   1. /search/suggest.json restricted to the variants.sku field, querying the
 *      prefixed SKU (e.g. "OUTLET-1234").
 *   2. Each candidate is verified by fetching its product .js and checking that
 *      a variant SKU matches exactly (case-insensitive) and is available.
 *      This avoids false positives from the search engine's partial matching.
 *
 * The element only renders something when a match is found, so there is no
 * layout shift when no outlet version exists.
 */
(function () {
  'use strict';

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || '{{ amount }}';

    function withDelimiters(number, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var value = '';
    var match = fmt.match(placeholderRegex);
    var token = match ? match[1] : 'amount';
    switch (token) {
      case 'amount':
        value = withDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', '');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    return fmt.replace(placeholderRegex, value);
  }

  function stripQuery(url) {
    return (url || '').split('?')[0];
  }

  class OutletProductNotice extends HTMLElement {
    connectedCallback() {
      if (this._loaded) return;
      this._loaded = true;

      this.rootUrl = (this.dataset.rootUrl || '').replace(/\/$/, '');
      this.moneyFormat = this.dataset.moneyFormat || '{{ amount }}';
      this.baseSku = (this.dataset.sku || '').trim().toUpperCase();
      // All three count as "outlet products"; we only need the SKU prefixes.
      this.prefixes = ['OUTLET-', 'DEMO-', 'RETUR-', 'RETURVARE-'];

      var run = this.init.bind(this);
      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 2000 });
      } else {
        setTimeout(run, 400);
      }
    }

    async init() {
      var sku = this.baseSku;
      if (!sku) return;
      // Don't run on an outlet product itself.
      if (this.prefixes.some((p) => sku.indexOf(p) === 0)) return;

      // Collect every matching outlet product across all prefixes, de-duped by handle.
      var byHandle = {};
      for (var i = 0; i < this.prefixes.length; i++) {
        try {
          var found = await this.findBySku(this.prefixes[i] + sku);
          found.forEach(function (m) {
            if (!byHandle[m.handle]) byHandle[m.handle] = m;
          });
        } catch (e) {
          /* network/parse errors are non-fatal */
        }
      }

      var matches = Object.keys(byHandle).map(function (h) {
        return byHandle[h];
      });
      if (matches.length) this.render(matches);
    }

    async findBySku(targetSku) {
      var params = new URLSearchParams();
      params.set('q', targetSku);
      params.set('resources[type]', 'product');
      params.set('resources[limit]', '10');
      params.set('resources[options][unavailable_products]', 'last');
      params.set('resources[options][fields]', 'variants.sku');

      var res = await fetch(this.rootUrl + '/search/suggest.json?' + params.toString(), {
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) return [];

      var data = await res.json();
      var products =
        (data.resources && data.resources.results && data.resources.results.products) || [];

      // Verify the first few candidates (search matches partially); keep exact hits.
      var matches = [];
      for (var i = 0; i < Math.min(products.length, 5); i++) {
        var verified = await this.verify(products[i], targetSku.toUpperCase());
        if (verified) matches.push(verified);
      }
      return matches;
    }

    async verify(suggestProduct, targetSkuU) {
      var handle =
        suggestProduct.handle ||
        stripQuery(suggestProduct.url || '')
          .split('/')
          .pop();
      if (!handle) return null;

      var res = await fetch(this.rootUrl + '/products/' + handle + '.js', {
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) return null;

      var full = await res.json();
      var variant = (full.variants || []).find(function (v) {
        return (v.sku || '').trim().toUpperCase() === targetSkuU && v.available;
      });
      if (!variant) return null;

      return {
        handle: handle,
        url: this.rootUrl + (full.url || '/products/' + handle),
        priceCents: variant.price
      };
    }

    render(matches) {
      // Cheapest outlet product drives the "fra <price>" line and the link target.
      matches.sort(function (a, b) {
        return a.priceCents - b.priceCents;
      });
      var cheapest = matches[0];
      var count = matches.length;
      var plural = count > 1;

      var noun = plural ? this.dataset.nounPlural : this.dataset.nounSingular;
      var avail = plural ? this.dataset.availPlural : this.dataset.availSingular;
      var cta = plural ? this.dataset.ctaPlural : this.dataset.ctaSingular;
      var from = this.dataset.from || '';
      var price = formatMoney(cheapest.priceCents, this.moneyFormat);

      var text =
        count +
        ' ' +
        noun +
        ' ' +
        avail +
        ' ' +
        from +
        ' <span class="outlet-notice__price">' +
        price +
        '</span>';

      this.innerHTML =
        '<a class="outlet-notice__box" href="' +
        cheapest.url +
        '">' +
        '<span class="outlet-notice__text">' +
        text +
        '</span>' +
        '<span class="outlet-notice__cta">' +
        cta +
        '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>' +
        '</span>' +
        '</a>';
      this.hidden = false;
    }
  }

  if (!customElements.get('outlet-product-notice')) {
    customElements.define('outlet-product-notice', OutletProductNotice);
  }
})();
